import { useRef, useEffect, useLayoutEffect, useCallback } from 'react';

import {
  debounce,
  getActiveNavItemHref,
  isNavItemActive,
} from '../01-core/Utilities';
import { isProgrammaticScroll } from '../01-core/ProgrammaticScroll';
import { OptionalLink, OptionalLinkProps } from '../01-core/OptionalLink';
import { ButtonProps } from '../02-atoms/Button';
import { Logo, LogoProps } from '../02-atoms/Logo';
import { Icon } from '../02-atoms/Icon';
import { ButtonGroup } from '../03-molecules/ButtonGroup';
import { useLocation } from 'react-router-dom';

export interface LanguageItemProps extends OptionalLinkProps {
  /** The label */
  label: string;
  /** Whether the item is active */
  active?: boolean;
}

export interface NavigationItemProps extends OptionalLinkProps {
  /** The label */
  label: string;
  /** The optional navigation title (short title for navigation) */
  nav_title?: string;
  /** The optional icon */
  icon?: string;
  /** Whether the item is active */
  active?: boolean;
  /** Whether to add a separator after the item */
  separate?: boolean;
  /** The optional child items */
  items?: NavigationItemProps[];
}

export interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** The logo */
  logo: LogoProps;
  /** The optional navigation items */
  items?: NavigationItemProps[];
  /** The optional actions */
  actions?: ButtonProps[];
  /** Languages */
  languages?: LanguageItemProps[];
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Distance to scroll in one direction before the header follows. Hiding stays
// deliberate, revealing reacts to the first upward impulse — on mobile the
// header state also drives the anchor navigation, which has to come back
// without the reader scrolling several times (#1750).
const THRESHOLD_DOWN = 100;
const THRESHOLD_UP = 10;

/** A block rendering the header */
export const Header = ({
  logo,
  items,
  actions,
  languages,
  ...props
}: HeaderProps) => {
  const ref = useRef<HTMLElement>(null);
  const wrapper = useRef<HTMLElement>(null);

  const lastPosition: React.MutableRefObject<number> = useRef(0);
  const lastDirectionPosition: React.MutableRefObject<number> = useRef(0);
  const lastDirection: React.MutableRefObject<string | false> = useRef(false);
  const location = useLocation();

  const pathname = location.pathname || '';
  const safeItems = items ?? [];

  const activeHref =
    safeItems.length && pathname
      ? getActiveNavItemHref(safeItems, pathname)
      : null;

  const activeIndex = safeItems.findIndex((item) =>
    isNavItemActive(item, activeHref),
  );

  const onScroll = useCallback(() => {
    if (!ref.current || !wrapper.current) return;
    const pos =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      (document.body && (document.body as HTMLBodyElement).scrollTop) ||
      0;
    const triggerPosition = wrapper.current.getBoundingClientRect().height;
    if (pos > triggerPosition) {
      // A deliberate jump must not move the header: it would push the anchor
      // navigation the reader just used off-screen, and leave the space that
      // scroll-margin-top reserves for it empty (#1778). Only the direction
      // evaluation pauses — the reset below stays reachable.
      if (!isProgrammaticScroll()) {
        const direction: 'up' | 'down' =
          pos < lastPosition.current ? 'up' : 'down';
        const other: 'up' | 'down' = direction === 'up' ? 'down' : 'up';
        if (lastDirection.current !== direction) {
          lastDirectionPosition.current = pos;
          lastDirection.current = direction;
        } else if (
          Math.abs(pos - lastDirectionPosition.current) >
            (direction === 'up' ? THRESHOLD_UP : THRESHOLD_DOWN) &&
          !ref.current.classList.contains('header--scrolling-' + direction)
        ) {
          if (!ref.current.classList.contains('header--scrolling-' + other)) {
            ref.current.classList.add('header--scrolling-immediate');
          }
          ref.current.classList.add('header--scrolling-' + direction);
          ref.current.classList.remove('header--scrolling-' + other);
          if (ref.current.classList.contains('header--scrolling-immediate')) {
            ref.current.getBoundingClientRect();
            ref.current.classList.remove('header--scrolling-immediate');
          }
          document
            .getElementsByTagName('body')[0]
            .classList.toggle('header-visible', direction === 'up');
        }
      }
    } else if (
      !ref.current.classList.contains('header--scrolling-up') ||
      pos === 0
    ) {
      document
        .getElementsByTagName('body')[0]
        .classList.remove('header-visible');
      ref.current.classList.remove('header--scrolling-up');
      ref.current.classList.remove('header--scrolling-down');
    }
    lastPosition.current = pos;
  }, [ref, wrapper]);

  const onResize = useCallback(() => {
    if (!ref.current || !wrapper.current) return;
    const height = wrapper.current.getBoundingClientRect().height;
    const measured = Math.floor(height) + 'px';
    const body = document.getElementsByTagName('body')[0];
    body.style.setProperty('--header--height', measured);
    body.style.setProperty('--header--measured', measured);
    onScroll();
  }, [ref, wrapper, onScroll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(
      () => {
        onResize();
      },
      100,
      false,
    ),
    [onResize],
  ); // some debounce typings expect the 3rd arg

  useIsomorphicLayoutEffect(() => {
    window.addEventListener(
      'scroll',
      onScroll as EventListener,
      { passive: true } as AddEventListenerOptions,
    );
    window.addEventListener('resize', handleResize as unknown as EventListener);
    onResize();
    return () => {
      window.removeEventListener(
        'resize',
        handleResize as unknown as EventListener,
      );
      window.removeEventListener('scroll', onScroll as EventListener);
      const body = document.getElementsByTagName('body')[0];
      body.style.removeProperty('--header--height');
      body.style.removeProperty('--header--measured');
    };
  }, [ref, wrapper, handleResize, onScroll, onResize]);

  return (
    <header
      ref={ref as React.RefObject<HTMLElement>}
      className="header"
      {...props}
    >
      <nav
        ref={wrapper as React.RefObject<HTMLElement>}
        className="header__wrapper"
      >
        <div className="header__container">
          <Logo className="header__logo" {...logo} />
          {languages && (
            <ul className="header__languages">
              {languages.map((item, i) => {
                return (
                  <li key={i}>
                    <OptionalLink
                      className={item.active ? 'header__active-language' : ''}
                      button={true}
                      {...item}
                    >
                      {item.label}
                    </OptionalLink>
                  </li>
                );
              })}
            </ul>
          )}
          {actions && (
            <ButtonGroup
              className="header__actions"
              compact={true}
              type="primary"
              items={actions}
            />
          )}
          {safeItems.length > 0 && (
            <ul className="header__nav header--large-only">
              {safeItems.map((item, i) => {
                const isActive = i === activeIndex;

                return (
                  <li key={i} className="header__nav-item">
                    <OptionalLink
                      className={[
                        'header__nav-link',
                        item.separate && 'header__nav-link--separate',
                        isActive && 'header__nav-link--active',
                        item.className,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      href={item.href}
                      title={item.title}
                      onClick={item.onClick}
                    >
                      {item.nav_title || item.label}
                      {item.icon && <Icon icon={item.icon} />}
                    </OptionalLink>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </nav>
    </header>
  );
};
